/** Number / time formatting shared across the UI. */

/** Compact money, matching Torn's own conventions: $1.23b, $4.56m, $78k. */
export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1e9) return `$${(value / 1e9).toFixed(2)}b`;
  if (abs >= 1e6) return `$${(value / 1e6).toFixed(2)}m`;
  if (abs >= 1e4) return `$${Math.round(value / 1e3)}k`;
  return `$${Math.round(value).toLocaleString()}`;
}

/** Full precision with separators, for tooltips and inputs. */
export function moneyExact(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `$${Math.round(value).toLocaleString()}`;
}

export function integer(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Math.round(value).toLocaleString();
}

/** Digits only, for controlled numeric text inputs. */
export function digitsOnly(raw: string): string {
  return raw.replace(/[^0-9]/g, "");
}

export function groupDigits(raw: string | number): string {
  const digits = (typeof raw === "number" ? String(Math.trunc(raw)) : raw).replace(/[^0-9]/g, "");
  if (digits === "") return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** Signed percentage; margins are negative when the price is a discount. */
export function percent(fraction: number | null | undefined, digits = 1): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return "—";
  const pct = fraction * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(digits)}%`;
}

export function relativeTime(epochMs: number | null | undefined, now = Date.now()): string {
  if (!epochMs) return "never";
  const seconds = Math.max(0, Math.round((now - epochMs) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Coerce weav3r's string-typed numerics without turning "" into 0. */
export function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : undefined;
}
